import React, { useState } from 'react';
import './BookingForm.css';

const BookingForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    bookingDate: '',
    bookingTime: '',
    occasion: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  // Get webhook URL from environment variable
  const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Trim whitespace for text inputs (except for phone numbers which may have spaces)
    const processedValue = name === 'phoneNumber' ? value : value.trimStart();
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[\d\s\-+()]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.bookingDate) {
      newErrors.bookingDate = 'Booking date is required';
    } else {
      const selectedDate = new Date(formData.bookingDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.bookingDate = 'Booking date cannot be in the past';
      }
    }

    if (!formData.bookingTime) {
      newErrors.bookingTime = 'Booking time is required';
    }

    if (!formData.occasion.trim()) {
      newErrors.occasion = 'Occasion is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setErrorMessage('');

    // Check if webhook URL is configured
    if (!WEBHOOK_URL || WEBHOOK_URL === 'YOUR_N8N_WEBHOOK_URL_HERE') {
      setSubmitStatus('error');
      setErrorMessage('Service configuration error. Please contact the administrator.');
      if (process.env.NODE_ENV === 'development') {
        console.error('Webhook URL not configured:', WEBHOOK_URL);
      }
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        FullName: formData.fullName.trim(),
        PhoneNumber: formData.phoneNumber.trim(),
        Email: formData.email.trim().toLowerCase(),
        BookingDate: formData.bookingDate,
        BookingTime: formData.bookingTime,
        Occasion: formData.occasion,
        SubmittedAt: new Date().toISOString()
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('Submitting to webhook:', WEBHOOK_URL);
        console.log('Payload:', payload);
      }

      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('Response status:', response.status);
      }

      if (response.ok) {
        setSubmitStatus('success');
        // Reset form after successful submission
        setFormData({
          fullName: '',
          phoneNumber: '',
          email: '',
          bookingDate: '',
          bookingTime: '',
          occasion: ''
        });
        // Clear any previous errors
        setErrors({});
      } else {
        const errorText = await response.text();
        if (process.env.NODE_ENV === 'development') {
          console.error('Response error:', response.status, errorText);
        }
        throw new Error(`Server error: ${response.status}`);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting form:', error);
      }
      setSubmitStatus('error');
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setErrorMessage('Network error: Unable to connect to the server. Please check your internet connection and try again.');
      } else if (error.message.includes('Server error')) {
        setErrorMessage('The server encountered an error. Please try again later or contact us directly.');
      } else {
        setErrorMessage('Unable to submit your booking. Please try again or contact us directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="booking-form-container">
      <div className="booking-form-card">
        <h1 className="form-title">Restaurant Booking</h1>
        <p className="form-subtitle">Please fill in your details to make a reservation</p>

        <form onSubmit={handleSubmit} className="booking-form">
          <div className="form-group">
            <label htmlFor="fullName">Full Name *</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={errors.fullName ? 'error' : ''}
              placeholder="Enter your full name"
            />
            {errors.fullName && <span className="error-message">{errors.fullName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className={errors.phoneNumber ? 'error' : ''}
              placeholder="Enter your phone number"
            />
            {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error' : ''}
              placeholder="Enter your email address"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bookingDate">Booking Date *</label>
              <input
                type="date"
                id="bookingDate"
                name="bookingDate"
                value={formData.bookingDate}
                onChange={handleChange}
                className={errors.bookingDate ? 'error' : ''}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.bookingDate && <span className="error-message">{errors.bookingDate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="bookingTime">Booking Time *</label>
              <input
                type="time"
                id="bookingTime"
                name="bookingTime"
                value={formData.bookingTime}
                onChange={handleChange}
                className={errors.bookingTime ? 'error' : ''}
              />
              {errors.bookingTime && <span className="error-message">{errors.bookingTime}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="occasion">Occasion *</label>
            <select
              id="occasion"
              name="occasion"
              value={formData.occasion}
              onChange={handleChange}
              className={errors.occasion ? 'error' : ''}
            >
              <option value="">Select an occasion</option>
              <option value="Birthday">Birthday</option>
              <option value="Anniversary">Anniversary</option>
              <option value="Business Meeting">Business Meeting</option>
              <option value="Date Night">Date Night</option>
              <option value="Family Gathering">Family Gathering</option>
              <option value="Celebration">Celebration</option>
              <option value="Other">Other</option>
            </select>
            {errors.occasion && <span className="error-message">{errors.occasion}</span>}
          </div>

          {submitStatus === 'success' && (
            <div className="success-message">
              ✓ Booking submitted successfully! We'll contact you soon.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="error-message-box">
              ✗ {errorMessage || 'Failed to submit booking. Please try again or contact us directly.'}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Book Now'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookingForm;

